// 券的格式化
import { UserCouponTransState, UserSPCouponTransState, UserCouponSpType, } from '@tencent/wphk-act-base-cgi';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';
import { ActType, ActivityCouponState, ChainDisplayCouponState, CouponActRelation, } from '../../types';
import { CouponState, ExchangeState } from '@tencent/wphk-act-base-types';
/** 生成百分比 */
const formatPercent = (sent, total) => {
    const result = ((total - sent) / total) * 100;
    return String(Math.min(100, Math.max(1, result))).split('.')[0];
};
/** 格式化日限额 */
const formatDayLimitSent = (coupon) => ({
    dayLimit: coupon.dayLimit || 99999999,
    daySent: coupon.daySent || 0,
});
/**
 * 是否是日限额的券
 * @param execAct
 * @returns
 */
function isDayLimitCoupon(coupon) {
    const { dayLimit, daySent } = formatDayLimitSent(coupon);
    return !(dayLimit === 99999999 && daySent === 0);
}
/**
 * 获取优惠券余量百分比
 */
function getAvailablePercent(coupon) {
    // totalNum 兑换活动最大参与总次数（所有用户）
    // totalSendNum 兑换活动已参与总次数（所有用户）
    const { totalNum, totalSendNum } = coupon;
    // 平台当日兑换上限次数
    // 平台当日已兑换次数
    const { dayLimit, daySent } = formatDayLimitSent(coupon);
    if (isDayLimitCoupon(coupon)) {
        // 有日限额的券
        return formatPercent(daySent, dayLimit);
    }
    // 没有日限额的券
    return formatPercent(totalSendNum, totalNum);
}
/**
 * 格式化券信息（格式接口返回的数据）
 * @param actType 活动类型
 * @param couponActRelation 券活动对应关系
 * @param stampInfo 印花信息（印花兑换活动需要）
 * @param data 接口数据
 * @returns
 */
export function formatExchActPrizeInfo(params) {
    const { actType, couponActRelation, stampInfo } = params;
    const { couponInfoList, exchActidList, qualInfoList } = params.data;
    // 最终券数组
    const couponList = [];
    // 格式校验
    if (!couponInfoList || !exchActidList)
        return couponList;
    if (couponActRelation === CouponActRelation.oneForMore) {
        /**
         * 单券对应多个兑换活动（恒生）
         * 1. 遍历兑换活动
         * 2. 通过actId 和 enActid找到对应的券信息
         */
        exchActidList.forEach((enExAct) => {
            // 从活动列表中找对应的券信息
            const coupon = couponInfoList.find((coupon) => coupon.actId === enExAct.enActid);
            const couponItem = getCouponItem({ enExAct, coupon, actType, qualInfoList, stampInfo });
            couponList.push(couponItem);
        });
    }
    else if (couponActRelation === CouponActRelation.oneForOne) {
        /**
         * 单券对应单个兑换活动（狂赏印）
         * 1. 遍历券列表
         * 2. 通过prize_id 和 en_couponid找到对应的券信息
         */
        couponInfoList.forEach((coupon) => {
            const enExAct = exchActidList.find((act) => act.prizeId === coupon.enCouponid);
            if (enExAct) {
                const couponItem = getCouponItem({ enExAct, coupon, actType, qualInfoList, stampInfo });
                couponList.push(couponItem);
            }
        });
    }
    return couponList;
}
/**
 * 组合单券
 * @param enExAct 活动
 * @param coupon 券
 * @param actType 活动类型
 * @param qualInfoList 资格信息
 * @param stampInfo 印花信息
 */
function getCouponItem({ enExAct, coupon, actType, qualInfoList, stampInfo, }) {
    var _a;
    let couponItem = Object.assign(Object.assign({}, enExAct), { couponId: (coupon === null || coupon === void 0 ? void 0 : coupon.couponId) || '', hasGot: (coupon === null || coupon === void 0 ? void 0 : coupon.hasGot) || 0, curSysTime: getServerTime() });
    // 如果是印花兑换活动类，还需要返回两个字段：qualId和stampRemainCnt
    if (actType === ActType.stampQualLimit) {
        const qualInfo = (qualInfoList || []).find(item => (enExAct === null || enExAct === void 0 ? void 0 : enExAct.enActid) === item.enActid);
        // FIXME: enExAct.qualCnt 应该也在这里，不应该在上面 enExAct 里面
        couponItem = Object.assign(couponItem, {
            qualId: (qualInfo === null || qualInfo === void 0 ? void 0 : qualInfo.qualId) || '',
            stampRemainCnt: ((_a = stampInfo === null || stampInfo === void 0 ? void 0 : stampInfo.stampData) === null || _a === void 0 ? void 0 : _a.userRemainCount) || 0, // 兑换活动才会需要
        });
    }
    return couponItem;
}
/**
 * 格式化券
 * @deprecated — core/format-coupon.ts/transformCouponsV2函数替代
 * @param couponList Activity.couponList
 * @param userCouponList Activity.userCouponList
 * @param couponStateSort 券状态排序
 * @param extramInfoList 券额外的信息
 * @returns
 */
export function transformCoupons({ couponList, userCouponList, couponStateSort, lockInfo, extraInfoList, }) {
    // 检查传入的couponStateSort格式
    if (!checkCouponStateSort(couponStateSort)) {
        throw new Error('couponStateSort传入格式错误，以下状态三选一：ActivityCouponState.received，ActivityCouponState.monthUserLimit，ActivityCouponState.totalUserLmit');
    }
    return (couponList || []).map((coupon) => {
        const extraCouponInfo = (extraInfoList || []).find(extraCoupon => extraCoupon.couponId === coupon.couponId);
        const userCouponInfo = (userCouponList || []).find(userCoupon => userCoupon.couponId === coupon.prizeId);
        return Object.assign(Object.assign(Object.assign({ couponId: coupon.couponId }, getCouponState({ coupon, userCouponInfo, couponStateSort, lockInfo })), { 
            // 只有电子优惠券userCouponid，商户展示券userScid
            // @ts-ignore
            userCouponid: (userCouponInfo === null || userCouponInfo === void 0 ? void 0 : userCouponInfo.userCouponid) || (userCouponInfo === null || userCouponInfo === void 0 ? void 0 : userCouponInfo.userScid), 
            // 领券参数
            getCouponParams: {
                qualId: coupon.qualId,
                enActid: coupon.enActid,
                prizeId: coupon.prizeId, // 奖品加密串
            }, availablePercent: getAvailablePercent(coupon) }), extraCouponInfo);
    });
}
/** 是否是CP券 */
function isCpType(userCouponInfo) {
    // 因为userCouponInfo的 couponId 为加密串，所以没有办法判断是 CP 还是 SP；
    // 所以这里认为 1、没有 couponType 则认为是 CP 券，否则认为是 SP 券；2、有 couponType 且 couponType 非展示券（线上日志有 couponType 为 1 的 CP 券）
    return (!(userCouponInfo === null || userCouponInfo === void 0 ? void 0 : userCouponInfo.couponType) ||
        ![UserCouponSpType.CDKEY, UserCouponSpType.NO_CDKEY, UserCouponSpType.SINGLE_CDKEY].includes(userCouponInfo.couponType));
}
/** CP、SP 的 transtate 是否为已过期 */
function isTranStateExpired(userCouponInfo) {
    if (!(userCouponInfo === null || userCouponInfo === void 0 ? void 0 : userCouponInfo.transState))
        return false;
    // 如果是 CP 券
    if (isCpType(userCouponInfo)) {
        return [UserCouponTransState.EXPIRED].includes(userCouponInfo.transState);
    }
    return [UserSPCouponTransState.EXPIRED].includes(userCouponInfo.transState);
}
/** CP、SP 的 transtate 是否为已使用 */
function isTranStateUsed(userCouponInfo) {
    if (!(userCouponInfo === null || userCouponInfo === void 0 ? void 0 : userCouponInfo.transState))
        return false;
    // 如果是 CP 券
    if (isCpType(userCouponInfo)) {
        return [UserCouponTransState.USED, UserCouponTransState.REFUND].includes(userCouponInfo.transState);
    }
    return [UserSPCouponTransState.USED, UserSPCouponTransState.OPENED].includes(userCouponInfo.transState);
}
/**
 * 处理券状态
 * @param coupon 券
 * @param couponStateSort 单券状态排序
 * @param lockInfo 时间锁信息
 * @returns
 */
function getCouponState({ coupon, userCouponInfo, couponStateSort, lockInfo, }) {
    // 根据券状态排序返回对应的状态
    for (const state of couponStateSort) {
        // 已过期，用户已领的券已经过期，且券没有回滚才被认为是真正的已过期
        if (state === ActivityCouponState.expired
            && coupon.hasGot >= coupon.userLimit
            && isTranStateExpired(userCouponInfo)) {
            return { couponState: CouponState.expired };
        }
        // 已兑罄(奖品维度-总限额)
        if (state === ActivityCouponState.totalLimit && coupon.totalSendNum >= coupon.totalNum) {
            return { exchangeState: ExchangeState.totalLimit };
        }
        // 今日已领完(奖品维度-日限额)
        if (state === ActivityCouponState.dayLimit && (coupon.daySent || 0) >= (coupon.dayLimit || 99999999)) {
            return { exchangeState: ExchangeState.dayLimit };
        }
        // 已领取
        if ([
            ActivityCouponState.received,
            ActivityCouponState.monthUserLimit,
            ActivityCouponState.totalUserLmit,
        ].includes(state)
            && (coupon.hasGot >= coupon.userLimit || (coupon.userMonthSent || 0) >= (coupon.userMonthLimit || 99999999))) {
            // 月用户限额
            if (couponStateSort.includes(ActivityCouponState.monthUserLimit)) {
                return { exchangeState: ExchangeState.monthUserLimit };
            }
            // 总用户限额
            if (couponStateSort.includes(ActivityCouponState.totalUserLmit)) {
                return { exchangeState: ExchangeState.totalUserLmit };
            }
            // 已领取
            return { couponState: CouponState.received };
        }
        // 已使用
        if (state === ActivityCouponState.used && isTranStateUsed(userCouponInfo)) {
            return { couponState: CouponState.used };
        }
        // 资格印花不足
        if (state === ActivityCouponState.qualNoEnough && coupon.qualCnt > (coupon.stampRemainCnt || 0)) {
            return { exchangeState: ExchangeState.qualNoEnough };
        }
        // 未开始（显示锁）
        if (state === ActivityCouponState.notStarted
            && (lockInfo === null || lockInfo === void 0 ? void 0 : lockInfo.isShowLock)
            && dayjs(coupon.curSysTime).isAfter(lockInfo.lockStartTime)
            && dayjs(coupon.curSysTime).isBefore(lockInfo.lockEndTime)) {
            return { exchangeState: ExchangeState.notStarted };
        }
        if ([ActivityCouponState.avaliable, ActivityCouponState.notReceived].includes(state) && (coupon.hasGot < coupon.userLimit && ((coupon.userMonthSent || 0) < (coupon.userMonthLimit || 99999999)))) {
            return state === ActivityCouponState.avaliable
                ? { exchangeState: ExchangeState.avaliable }
                : { couponState: CouponState.notReceived };
        }
    }
    // 可兑换
    if (couponStateSort.includes(ActivityCouponState.avaliable)) {
        return { exchangeState: ExchangeState.avaliable };
    }
    // 未领取
    return { couponState: CouponState.notReceived };
}
/** 检查券状态排序输入 */
function checkCouponStateSort(couponStateSort) {
    let flag = 0;
    for (const state of couponStateSort) {
        if (state === ActivityCouponState.received
            || state === ActivityCouponState.monthUserLimit
            || state === ActivityCouponState.totalUserLmit) {
            flag += 1;
            continue;
        }
    }
    return flag <= 1;
}
/**
 * 处理并列式连锁券
 */
export function getDisplayCouponState(chainCoupon, actInfo, curCanAvaliableCouponId, curSysTime) {
    /** 已领取 */
    if (chainCoupon.transState === +CouponState.received) {
        // 判断时间:大于validtime小于expiretime，则是已领取；大于expiretime时间，就是可领取；小于validtime，则是未可用
        if (dayjs(curSysTime).isBefore(chainCoupon.validTime)) {
            return ChainDisplayCouponState.noCanUse;
        }
        if (dayjs(curSysTime).isAfter(chainCoupon.expireTime)) {
            return ChainDisplayCouponState.notReceived;
        }
        return ChainDisplayCouponState.received;
    }
    /** 已使用 */
    if (chainCoupon.transState === +CouponState.used) {
        return ChainDisplayCouponState.used;
    }
    // 已兑罄(奖品维度-总限额)
    if (actInfo.totalSendNum >= actInfo.totalNum) {
        if (chainCoupon.couponId === curCanAvaliableCouponId) {
            return ChainDisplayCouponState.totalLimit;
        }
    }
    // 今日已领完(奖品维度-日限额)
    if ((actInfo.daySent || 0) >= (actInfo.dayLimit || 99999999)) {
        // 券是可领时才展示库存
        if (chainCoupon.couponId === curCanAvaliableCouponId) {
            return ChainDisplayCouponState.dayLimit;
        }
    }
    /** 未领取/已过期，当作可领取 */
    if (chainCoupon.transState === +CouponState.notReceived || chainCoupon.transState === +CouponState.expired) {
        if (chainCoupon.couponId !== curCanAvaliableCouponId) {
            return ChainDisplayCouponState.locked;
        }
        // 如果是库存已经没了，就展示今日限额。因为券是可能回收的
        if (chainCoupon.dispatchCount >= chainCoupon.totalCount) {
            return ChainDisplayCouponState.dayLimit;
        }
        return ChainDisplayCouponState.notReceived;
    }
    // 默认不可领
    return ChainDisplayCouponState.locked;
}
